import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import { Chart, elements, registerables } from "chart.js";
import { BeatLoader } from "react-spinners";
import "../../../../../styles/count_graphs.css";
import {
  BiFullscreen,
  BiExitFullscreen,
  BiLineChart,
  BiBarChartAlt2,
} from "react-icons/bi";
import { peopleOccupancyColor, lprOccupancyColor } from "../../../../../config";

const DailyOccupancyChart = ({
  selectedYear,
  dailyOccupancyData,
  dailyDataLoading,
  selectedMonth,
  selectedWeek,
  selectedWeekStartDate,
  relativeWeekNumber,
  selectedDate,
  selectedDayOfWeek,
  dailyPercentageIncrease,
  dailyAlerts,
  occupancyType,
}) => {
  const [barChartData, setBarChartData] = useState();
  const [lineChartData, setLineChartData] = useState();
  const [chartType, setChartType] = useState("line");
  const barChartRef = useRef(null);
  const lineChartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const smallScreen = window.innerWidth < 786;

  // Get the appropriate color based on occupancy type
  const getOccupancyColor = () => {
    return occupancyType === "LPR" ? lprOccupancyColor : peopleOccupancyColor;
  };

  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1];
  };

  const prepareDailyOccupancyLineChart = () => {
    const color = getOccupancyColor();
    return {
      labels: dailyOccupancyData.map((d) => d.day),
      datasets: [
        {
          label: "Daily Occupancy Alerts",
          data: dailyOccupancyData.map((d) => d.total_count),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 3,
          tension: 0.3,
          spanGaps: true,
        },
      ],
    };
  };

  const getDailyOccupancyLineChartOptions = () => {
    return {
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      elements: {
        points: {
          radius: 4,
          hoverRadius: 6,
        },
        line: {
          tension: 0.3,
        },
      },
      responsive: true, // Ensure the chart is responsive
      maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
      scales: {
        x: {
          ticks: {
            color: (context) => {
              // Change this to accomodate multiple polygons per month.
              const labelDay = dailyOccupancyData[context.index]?.day;
              return labelDay === selectedDayOfWeek ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const labelDay = dailyOccupancyData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? "bold" : "normal";
              },
              size: (context) => {
                const labelDay = dailyOccupancyData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? 14 : 12;
              },
            },
          },
          title: {
            display: true,
            text: "Days of the week",
            color: "white",
          },
          grid: {
            display: true,
            drawBorder: true,
            drawOnChartArea: false,
            color: "#aaa",
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: "#F8F8F8",
          },
          title: {
            display: true,
            text: "Occupancy Alerts",
            color: "white",
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "nearest", // Show tooltip for all datasets at the same index
        },
        legend: {
          position: "top", // Position the legend at the top
          labels: {
            color: "white",
            font: {
              size: smallScreen ? 8 : 14, // Adjust font size of legend labels
            },
            boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
            boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
          },
        },
      },
    };
  };

  const prepareDailyOccupancyBarChart = () => {
    const color = getOccupancyColor();
    return {
      labels: dailyOccupancyData.map((d) => d.day),
      datasets: [
        {
          label: "Daily Occupancy Alerts",
          data: dailyOccupancyData.map((d) => d.total_count),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
        },
      ],
    };
  };

  const getDailyOccupancyBarChartOptions = () => {
    return {
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      responsive: true, // Ensure the chart is responsive
      maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: (context) => {
              // Change this to accomodate multiple polygons per month.
              const labelDay = dailyOccupancyData[context.index]?.day;
              return labelDay === selectedDayOfWeek ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const labelDay = dailyOccupancyData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? "bold" : "normal";
              },
              size: (context) => {
                const labelDay = dailyOccupancyData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? 14 : 12;
              },
            },
          },
          title: {
            display: true,
            text: "Days of the week",
            color: "white",
          },
          grid: {
            display: true,
            drawBorder: true,
            drawOnChartArea: false,
            color: "#aaa",
          },
        },
        y: {
          ticks: {
            color: "#F8F8F8",
          },
          stacked: true,
          title: {
            display: true,
            text: "Occupancy Alerts",
            color: "white",
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "nearest", // Show tooltip for all datasets at the same index
        },
        legend: {
          position: "top", // Position the legend at the top
          labels: {
            color: "white",
            font: {
              size: smallScreen ? 8 : 14, // Adjust font size of legend labels
            },
            boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
            boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
          },
        },
      },
    };
  };

  const toggleChartType = () => {
    setChartType((prevType) => (prevType === "bar" ? "line" : "bar"));
  };

  useEffect(() => {
    if (dailyOccupancyData.length) {
      const preparedLineChart = prepareDailyOccupancyLineChart();
      if (preparedLineChart) {
        setLineChartData(preparedLineChart);
      }
      const preparedBarChart = prepareDailyOccupancyBarChart();
      if (preparedBarChart) {
        setBarChartData(preparedBarChart);
      }
    }
  }, [dailyOccupancyData]);

  Chart.register(...registerables);

  return (
    <div
      className={`count-bar-chart-wrapper ${
        isFullscreen ? "fullscreen-container" : ""
      }`}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {/* Title aligned left */}
        <div
          className="graph-title"
          style={{ flex: "1 1 auto", minWidth: "200px" }}
        >
          Daily {occupancyType} Occupancy
        </div>

        {/* Buttons and percentage aligned right */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            justifyContent: "flex-end",
            flex: "1 1 auto",
            minWidth: "100px",
          }}
        >
          {/* Chart toggle + fullscreen buttons */}
          <div style={{ display: "flex", gap: "10px" }}>
            <button className="toggle-chart-button" onClick={toggleChartType}>
              {chartType === "bar" ? (
                <BiLineChart
                  size={24}
                  title="Switch to Line Chart"
                  color="white"
                />
              ) : (
                <BiBarChartAlt2
                  size={24}
                  title="Switch to Bar Chart"
                  color="white"
                />
              )}
            </button>
            <button
              className="toggle-chart-button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <BiExitFullscreen size={24} color="white" />
              ) : (
                <BiFullscreen size={24} color="white" />
              )}
            </button>
          </div>

          {/* Percentage change */}
          <div className="count-chart-footer-percentage-container">
            <div className="count-chart-footer-percentage-icon">
              {dailyPercentageIncrease > 0 ? (
                <svg
                  style={{ color: "#32de84" }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-arrow-up-right-circle"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z"
                  />
                </svg>
              ) : (
                <svg
                  style={{ color: "#EF5B5B" }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-arrow-down-right-circle"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z"
                  />
                </svg>
              )}
            </div>
            <div
              className="count-chart-footer-percentage-value"
              style={{
                color: dailyPercentageIncrease > 0 ? "#32de84" : "#EF5B5B",
              }}
            >
              {dailyPercentageIncrease}%
            </div>
          </div>
        </div>
      </div>
      <div className="chart-total-container">
        <div className="chart-total-pill">
          <div
            className="chart-total-label"
            style={{ color: getOccupancyColor() }}
          >
            Total:{" "}
            {dailyOccupancyData?.length > 0
              ? dailyOccupancyData.reduce(
                  (sum, day) => sum + (day.total_count || 0),
                  0
                )
              : 0}
          </div>
        </div>
      </div>
      <div
        className={
          dailyDataLoading ? "monthly-bar-chart-container-loading" : ""
        }
      >
        {chartType === "line" ? (
          <Line
            ref={lineChartRef}
            data={lineChartData || { labels: [], datasets: [] }} // Fetch prepared data
            options={getDailyOccupancyLineChartOptions()}
          />
        ) : (
          <Bar
            ref={barChartRef}
            data={barChartData || { labels: [], datasets: [] }}
            options={getDailyOccupancyBarChartOptions()}
          />
        )}
        {dailyDataLoading && (
          <div id="monthly-loading-overlay">
            <BeatLoader color={"white"} loading={dailyDataLoading} size={15} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyOccupancyChart;
